import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RequestError } from "../../lib/api";
import { passkeyCancelled, passkeysSupported } from "../../lib/passkeys";
import { useSession } from "../../lib/session";
import { Button, SectionMessage } from "../../ui";
import { useDocumentTitle } from "../../ui/shell";
import { AuthLayout } from "./AuthLayout";
import styles from "./auth.module.css";

export function SignInPage() {
  const { t } = useTranslation();
  const { signIn } = useSession();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);
  const supported = passkeysSupported();

  useDocumentTitle(t("auth.signIn.documentTitle"));

  const start = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await signIn();
    } catch (error) {
      if (passkeyCancelled(error)) {
        setFailure({ title: t("auth.signIn.cancelledTitle"), message: t("auth.signIn.cancelled") });
      } else if (error instanceof RequestError && error.code === "network_error") {
        setFailure({ title: t("auth.signIn.connectionFailed"), message: t("auth.signIn.connectionFailedText") });
      } else if (error instanceof RequestError && error.status === 429) {
        setFailure({ title: t("auth.signIn.failed"), message: t("auth.signIn.rateLimited") });
      } else {
        setFailure({ title: t("auth.signIn.failed"), message: t("auth.signIn.unknownPasskey") });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.signIn.title")} subtitle={t("auth.signIn.subtitle")}>
      {!supported ? <SectionMessage tone="warning" title={t("auth.unsupportedTitle")}>{t("auth.unsupported")}</SectionMessage> : null}
      {failure ? (
        <SectionMessage tone="danger" title={failure.title}>
          {failure.message}
        </SectionMessage>
      ) : null}
      <div className={styles.form}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          disabled={!supported}
          iconBefore={<KeyRound size={16} strokeWidth={1.75} aria-hidden="true" />}
          onClick={() => void start()}
        >
          {busy ? t("auth.signIn.waiting") : t("auth.signIn.action")}
        </Button>
        <p className={styles.hint}>{t("auth.signIn.recovery")}</p>
      </div>
    </AuthLayout>
  );
}
